import { InvalidQuantityError, NotEligibleMint } from '../../errors.mjs';
import { minterAbiMap, MINT_GAS_LIMIT_MULTIPLIER, MINT_FALLBACK_GAS_LIMIT, NULL_ADDRESS } from '../../utils/constants.mjs';
import { validateAddress, BigIntMin, BigIntMax, exhaustiveGuard, assertIsHexList, scaleAmount } from '../../utils/helpers.mjs';
import { validateSoundEdition } from '../validation.mjs';
import { getMerkleProof } from './merkle.mjs';
import { isSchedulePaused } from './schedules.mjs';
import { interfaceIds } from '@soundxyz/sound-protocol/interfaceIds';
import { soundEditionV1_2Abi } from '../../abi/sound-edition-v1_2.mjs';

async function numberOfTokensOwned({
  editionAddress,
  userAddress,
  assumeValidSoundContract = false
}) {
  await validateSoundEdition.call(this, { editionAddress, assumeValidSoundContract });
  const { readContract } = await this.expectClient();
  validateAddress(editionAddress, {
    type: "SOUND_EDITION"
  });
  validateAddress(userAddress, {
    type: "WALLET"
  });
  return readContract({
    abi: soundEditionV1_2Abi,
    address: editionAddress,
    functionName: "balanceOf",
    args: [userAddress]
  }).then((value) => Number(value));
}
async function numberMinted({
  editionAddress,
  userAddress,
  assumeValidSoundContract = false
}) {
  await validateSoundEdition.call(this, { editionAddress, assumeValidSoundContract });
  const { readContract } = await this.expectClient();
  validateAddress(editionAddress, {
    type: "SOUND_EDITION"
  });
  validateAddress(userAddress, {
    type: "WALLET"
  });
  return readContract({
    abi: soundEditionV1_2Abi,
    address: editionAddress,
    functionName: "numberMinted",
    args: [userAddress]
  });
}
async function mintHelper({
  mintSchedule,
  quantity,
  affiliate = NULL_ADDRESS,
  gas,
  maxFeePerGas,
  maxPriorityFeePerGas,
  skipQuantityChecks = false,
  merkleProof,
  assumeValidSoundContract = false,
  chain
}) {
  await validateSoundEdition.call(this, { editionAddress: mintSchedule.editionAddress, assumeValidSoundContract });
  if (quantity <= 0 || Math.floor(quantity) !== quantity)
    throw new InvalidQuantityError({ quantity });
  const { userAddress, wallet } = await this.expectWallet();
  let eligibleMintQuantity;
  if (!skipQuantityChecks) {
    eligibleMintQuantity = await eligibleQuantity.call(this, {
      mintSchedule,
      userAddress
    });
    if (eligibleMintQuantity < quantity) {
      throw new NotEligibleMint({
        eligibleMintQuantity,
        mintSchedule,
        userAddress
      });
    }
  }
  const value = BigInt(mintSchedule.price * BigInt(quantity)) + mintSchedule.platformTransactionFee;
  const txnOverrides = {
    gas,
    maxFeePerGas,
    maxPriorityFeePerGas
  };
  validateAddress(mintSchedule.editionAddress, {
    type: "SOUND_EDITION"
  });
  validateAddress(affiliate, {
    type: "AFFILIATE"
  });
  validateAddress(mintSchedule.minterAddress, {
    type: "MINTER"
  });
  const sharedWriteContractParameters = {
    account: userAddress,
    address: mintSchedule.minterAddress,
    chain,
    functionName: "mint",
    value
  };
  const client = await this.expectClient();
  const interfaceId = mintSchedule.interfaceId;
  switch (interfaceId) {
    case interfaceIds.IRangeEditionMinter:
    case interfaceIds.IRangeEditionMinterV2:
    case interfaceIds.IRangeEditionMinterV2_1: {
      const args = [mintSchedule.editionAddress, BigInt(mintSchedule.mintId), quantity, affiliate];
      async function startTransaction() {
        const transactionHash = await wallet.writeContract({
          abi: minterAbiMap[interfaceIds.IRangeEditionMinter],
          ...sharedWriteContractParameters,
          ...txnOverrides,
          args
        });
        return {
          transactionHash
        };
      }
      if (txnOverrides.gas) {
        return {
          gasEstimate: txnOverrides.gas,
          startTransaction
        };
      }
      try {
        const gasEstimate = await client.estimateContractGas({
          abi: minterAbiMap[interfaceIds.IRangeEditionMinterV2_1],
          ...sharedWriteContractParameters,
          ...txnOverrides,
          args
        });
        txnOverrides.gas = scaleAmount({ amount: gasEstimate, multiplier: MINT_GAS_LIMIT_MULTIPLIER });
      } catch (err) {
        txnOverrides.gas = MINT_FALLBACK_GAS_LIMIT;
      }
      return {
        gasEstimate: txnOverrides.gas,
        startTransaction
      };
    }
    case interfaceIds.IMerkleDropMinter:
    case interfaceIds.IMerkleDropMinterV2:
    case interfaceIds.IMerkleDropMinterV2_1: {
      let proof;
      if (merkleProof === void 0) {
        const params = {
          address: mintSchedule.minterAddress,
          functionName: "mintInfo",
          args: [mintSchedule.editionAddress, mintSchedule.mintId]
        };
        const { merkleRootHash: merkleRoot } = await (interfaceId === interfaceIds.IMerkleDropMinter ? client.readContract({
          ...params,
          abi: minterAbiMap[interfaceId]
        }) : interfaceId === interfaceIds.IMerkleDropMinterV2 ? client.readContract({
          ...params,
          abi: minterAbiMap[interfaceId]
        }) : client.readContract({
          ...params,
          abi: minterAbiMap[interfaceId]
        }));
        proof = await getMerkleProof.call(this, {
          merkleRoot,
          userAddress
        });
      } else {
        proof = merkleProof;
      }
      if (!proof?.length) {
        throw new NotEligibleMint({
          mintSchedule,
          userAddress,
          eligibleMintQuantity
        });
      }
      assertIsHexList(proof);
      const args = [mintSchedule.editionAddress, mintSchedule.mintId, quantity, proof, affiliate];
      async function startTransaction() {
        const transactionHash = await wallet.writeContract({
          abi: minterAbiMap[interfaceIds.IMerkleDropMinterV2_1],
          ...sharedWriteContractParameters,
          ...txnOverrides,
          args
        });
        return {
          transactionHash
        };
      }
      if (txnOverrides.gas) {
        return {
          gasEstimate: txnOverrides.gas,
          startTransaction
        };
      }
      try {
        const gasEstimate = await client.estimateContractGas({
          abi: minterAbiMap[interfaceIds.IMerkleDropMinterV2_1],
          ...sharedWriteContractParameters,
          ...txnOverrides,
          args
        });
        txnOverrides.gas = scaleAmount({ amount: gasEstimate, multiplier: MINT_GAS_LIMIT_MULTIPLIER });
      } catch (err) {
        txnOverrides.gas = MINT_FALLBACK_GAS_LIMIT;
      }
      return {
        gasEstimate: txnOverrides.gas,
        startTransaction
      };
    }
    default: {
      exhaustiveGuard(interfaceId);
    }
  }
}
async function estimateMint(mintOptions) {
  return mintHelper.call(this, mintOptions).then(({ gasEstimate }) => gasEstimate);
}
async function mint(mintOptions) {
  return mintHelper.call(this, mintOptions).then(({ startTransaction }) => startTransaction());
}
async function mintToHelper({
  affiliate = NULL_ADDRESS,
  affiliateProof = [],
  attributonId = 0n,
  gas,
  maxFeePerGas,
  maxPriorityFeePerGas,
  mintSchedule,
  mintToAddress,
  quantity,
  chain,
  skipQuantityChecks = false,
  merkleProof,
  assumeValidSoundContract = false
}) {
  await validateSoundEdition.call(this, { editionAddress: mintSchedule.editionAddress, assumeValidSoundContract });
  if (quantity <= 0 || Math.floor(quantity) !== quantity)
    throw new InvalidQuantityError({ quantity });
  const { wallet, userAddress } = await this.expectWallet();
  const toAddress = mintToAddress ?? userAddress;
  validateAddress(toAddress, {
    type: "WALLET"
  });
  let eligibleMintQuantity;
  if (!skipQuantityChecks) {
    eligibleMintQuantity = await eligibleQuantity.call(this, {
      mintSchedule,
      userAddress
    });
    if (eligibleMintQuantity < quantity) {
      throw new NotEligibleMint({
        eligibleMintQuantity,
        mintSchedule,
        userAddress
      });
    }
  }
  validateAddress(mintSchedule.editionAddress, {
    type: "SOUND_EDITION"
  });
  validateAddress(affiliate, {
    type: "AFFILIATE"
  });
  validateAddress(mintSchedule.minterAddress, {
    type: "MINTER"
  });
  const value = BigInt(mintSchedule.price * BigInt(quantity)) + mintSchedule.platformTransactionFee;
  const txnOverrides = {
    gas,
    maxFeePerGas,
    maxPriorityFeePerGas
  };
  const sharedWriteContractParameters = {
    account: userAddress,
    address: mintSchedule.minterAddress,
    chain,
    functionName: "mintTo",
    value
  };
  const interfaceId = mintSchedule.interfaceId;
  switch (interfaceId) {
    case interfaceIds.IRangeEditionMinterV2:
    case interfaceIds.IRangeEditionMinterV2_1: {
      assertIsHexList(affiliateProof);
      const args = [
        mintSchedule.editionAddress,
        BigInt(mintSchedule.mintId),
        toAddress,
        quantity,
        affiliate,
        affiliateProof,
        attributonId
      ];
      async function startTransaction() {
        const transactionHash = await wallet.writeContract({
          abi: minterAbiMap[interfaceIds.IRangeEditionMinterV2_1],
          ...sharedWriteContractParameters,
          ...txnOverrides,
          args
        });
        return {
          transactionHash
        };
      }
      if (txnOverrides.gas) {
        return {
          gasEstimate: txnOverrides.gas,
          startTransaction
        };
      }
      try {
        const client = await this.expectClient();
        const gasEstimate = await client.estimateContractGas({
          abi: minterAbiMap[interfaceIds.IRangeEditionMinterV2_1],
          ...sharedWriteContractParameters,
          ...txnOverrides,
          args
        });
        txnOverrides.gas = scaleAmount({ amount: gasEstimate, multiplier: MINT_GAS_LIMIT_MULTIPLIER });
      } catch (err) {
        txnOverrides.gas = MINT_FALLBACK_GAS_LIMIT;
      }
      return {
        gasEstimate: txnOverrides.gas,
        startTransaction
      };
    }
    case interfaceIds.IMerkleDropMinterV2:
    case interfaceIds.IMerkleDropMinterV2_1: {
      let proof;
      if (merkleProof === void 0) {
        const client = await this.expectClient();
        const params = {
          address: mintSchedule.minterAddress,
          functionName: "mintInfo",
          args: [mintSchedule.editionAddress, mintSchedule.mintId]
        };
        const { merkleRootHash: merkleRoot } = await (interfaceId === interfaceIds.IMerkleDropMinterV2 ? client.readContract({
          ...params,
          abi: minterAbiMap[interfaceId]
        }) : client.readContract({
          ...params,
          abi: minterAbiMap[interfaceId]
        }));
        proof = await getMerkleProof.call(this, {
          merkleRoot,
          userAddress
        });
      } else {
        proof = merkleProof;
      }
      if (!proof?.length) {
        throw new NotEligibleMint({
          mintSchedule,
          userAddress,
          eligibleMintQuantity
        });
      }
      assertIsHexList(proof);
      assertIsHexList(affiliateProof);
      const args = [
        mintSchedule.editionAddress,
        mintSchedule.mintId,
        toAddress,
        quantity,
        // TODO, allow overriding allowlisted for delegatecash
        toAddress,
        proof,
        affiliate,
        affiliateProof,
        attributonId
      ];
      async function startTransaction() {
        const transactionHash = await wallet.writeContract({
          abi: minterAbiMap[interfaceIds.IMerkleDropMinterV2_1],
          ...sharedWriteContractParameters,
          ...txnOverrides,
          args
        });
        return {
          transactionHash
        };
      }
      if (txnOverrides.gas) {
        return {
          gasEstimate: txnOverrides.gas,
          startTransaction
        };
      }
      try {
        const client = await this.expectClient();
        const gasEstimate = await client.estimateContractGas({
          abi: minterAbiMap[interfaceIds.IMerkleDropMinterV2_1],
          ...sharedWriteContractParameters,
          ...txnOverrides,
          args
        });
        txnOverrides.gas = scaleAmount({ amount: gasEstimate, multiplier: MINT_GAS_LIMIT_MULTIPLIER });
      } catch (err) {
        txnOverrides.gas = MINT_FALLBACK_GAS_LIMIT;
      }
      return {
        gasEstimate: txnOverrides.gas,
        startTransaction
      };
    }
    default: {
      exhaustiveGuard(interfaceId);
    }
  }
}
async function estimateMintTo(mintOptions) {
  return mintToHelper.call(this, mintOptions).then(({ gasEstimate }) => gasEstimate);
}
async function mintTo(mintOptions) {
  return mintToHelper.call(this, mintOptions).then(({ startTransaction }) => startTransaction());
}
async function eligibleQuantity({
  mintSchedule,
  timestamp = Math.floor(Date.now() / 1e3),
  userAddress
}) {
  if (timestamp < mintSchedule.startTime || timestamp > mintSchedule.endTime || isSchedulePaused(mintSchedule)) {
    return 0n;
  }
  switch (mintSchedule.mintType) {
    case "RangeEdition": {
      const maxQty = timestamp < mintSchedule.cutoffTime ? mintSchedule.maxMintableUpper : mintSchedule.maxMintableLower;
      if (mintSchedule.totalMinted >= maxQty) {
        return 0n;
      }
      break;
    }
    case "MerkleDrop": {
      const merkleRoot = mintSchedule.merkleRoot;
      const proof = await getMerkleProof.call(this, {
        merkleRoot,
        userAddress
      });
      if (!proof?.length) {
        return 0n;
      }
      break;
    }
  }
  validateAddress(mintSchedule.editionAddress, {
    type: "SOUND_EDITION"
  });
  validateAddress(userAddress, {
    type: "WALLET"
  });
  validateAddress(mintSchedule.minterAddress, {
    type: "MINTER"
  });
  const { readContract } = await this.expectClient();
  const [editionTotalMinted, editionMaxMintable] = await Promise.all([
    readContract({
      abi: soundEditionV1_2Abi,
      address: mintSchedule.editionAddress,
      functionName: "totalMinted"
    }),
    readContract({
      abi: soundEditionV1_2Abi,
      address: mintSchedule.editionAddress,
      functionName: "editionMaxMintable"
    })
  ]);
  const editionRemainingQty = BigInt(editionMaxMintable) - editionTotalMinted;
  if (!editionRemainingQty) {
    return 0n;
  }
  const remainingForSchedule = (typeof mintSchedule.maxMintable === "function" ? mintSchedule.maxMintable(timestamp) : mintSchedule.maxMintable) - mintSchedule.totalMinted;
  const mintedByUserFromSchedule = await (mintSchedule.interfaceId === interfaceIds.IMerkleDropMinterV2 || mintSchedule.interfaceId === interfaceIds.IMerkleDropMinterV2_1 ? readContract({
    abi: minterAbiMap[interfaceIds.IMerkleDropMinterV2_1],
    address: mintSchedule.minterAddress,
    functionName: "mintCount",
    args: [mintSchedule.editionAddress, mintSchedule.mintId, userAddress]
  }) : numberMinted.call(this, {
    editionAddress: mintSchedule.editionAddress,
    userAddress
  }));
  const eligibleForUserOnSchedule = BigInt(mintSchedule.maxMintablePerAccount) - mintedByUserFromSchedule;
  const scheduleEligibleQty = BigIntMin(BigInt(remainingForSchedule), eligibleForUserOnSchedule);
  return BigIntMax(0n, BigIntMin(scheduleEligibleQty, editionRemainingQty));
}

export { eligibleQuantity, estimateMint, estimateMintTo, mint, mintTo, numberMinted, numberOfTokensOwned };
